import { useId, useState, type FormEvent } from 'react';
import type { Friend, HouseholdInviteOutcome } from '../../shared/api';
import { Avatar } from '../components/Avatar';
import { useCurrentUser } from '../hooks/useAuth';
import { useResource } from '../hooks/useResource';
import { api, errorMessage } from '../lib/api';
import { formatDate } from '../lib/format';

const OUTCOME_MESSAGES: Record<HouseholdInviteOutcome, (email: string) => string> = {
  sent: (email) => `Invitation sent to ${email}. They'll see it next time they sign in.`,
  already_sent: (email) => `You've already invited ${email}.`,
  already_member: (email) => `${email} is already in your household.`,
};

export function HouseholdPage() {
  const user = useCurrentUser();
  const { data, error, reload } = useResource('household', api.household);
  const friends = useResource('friends', api.listFriends);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setActionError(null);
    try {
      await action();
      reload();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function accept(id: number, from: string) {
    if (
      !window.confirm(
        `Join ${from}'s household? Your books and bottles become one shared collection: duplicates are merged, and everyone in it can change anything.`,
      )
    ) {
      return;
    }
    void act(() => api.acceptHouseholdInvite(id));
  }

  function leave() {
    if (!window.confirm('Leave the household? You keep what you added; the others keep what they added.')) return;
    void act(() => api.leaveHousehold());
  }

  if (!data) {
    return error ? (
      <div className="page-status" role="alert">
        <p className="error-text">{errorMessage(error)}</p>
        <button type="button" className="btn btn--primary" onClick={reload}>
          Try again
        </button>
      </div>
    ) : (
      <p className="page-status">Loading household…</p>
    );
  }

  const { members, incoming, outgoing } = data;
  // Friends who aren't in the household yet and haven't been invited: the likely housemates.
  const memberIds = new Set(members.map((m) => m.id));
  const invitedEmails = new Set(outgoing.map((invite) => invite.email));
  const suggestions = (friends.data?.friends ?? []).filter((f) => !memberIds.has(f.id) && !invitedEmails.has(f.email));

  return (
    <section className="friends-page">
      <h1>Household</h1>
      <p className="muted">
        People in one household share one collection: the same books, bar, categories and shopping list, and any of them can
        change anything. Friends of anyone in the household can see all of it. Invite someone by the email address they sign in
        with; when they accept, any books you both own are merged into one copy.
      </p>

      <InviteForm onSent={reload} suggestions={suggestions} />

      {actionError && (
        <p className="error-text" role="alert">
          {actionError}
        </p>
      )}

      {incoming.length > 0 && (
        <>
          <h2>Invitations for you</h2>
          <ul className="friend-list">
            {incoming.map((invite) => (
              <li key={invite.id} className="friend-list__item">
                <Avatar user={invite.from} className="friend-avatar" />
                <span className="friend-list__who">
                  <span className="friend-list__name">{invite.from.name ?? invite.from.email}</span>
                  <span className="muted">{invite.from.email}</span>
                </span>
                <span className="friend-list__actions">
                  <button
                    type="button"
                    className="btn btn--primary btn--small"
                    disabled={busy}
                    onClick={() => accept(invite.id, invite.from.name ?? invite.from.email)}
                  >
                    Join
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    disabled={busy}
                    onClick={() => void act(() => api.deleteHouseholdInvite(invite.id))}
                  >
                    Decline
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <h2>Your household</h2>
      {members.length === 0 ? (
        <p className="hint">Just you for now. Invite the people you live with.</p>
      ) : (
        <ul className="friend-list">
          {members.map((member) => (
            <li key={member.id} className="friend-list__item">
              <Avatar user={member} className="friend-avatar" />
              <span className="friend-list__who">
                <span className="friend-list__name">
                  {member.name ?? member.email}
                  {member.id === user.id && <span className="muted"> (you)</span>}
                </span>
                <span className="muted">Since {formatDate(member.joinedAt)}</span>
              </span>
              {member.id === user.id && (
                <span className="friend-list__actions">
                  <button type="button" className="btn btn--ghost btn--small btn--danger-text" disabled={busy} onClick={leave}>
                    Leave
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {outgoing.length > 0 && (
        <>
          <h2>Waiting for an answer</h2>
          <ul className="friend-list">
            {outgoing.map((invite) => (
              <li key={invite.id} className="friend-list__item">
                <span className="friend-list__who">
                  <span className="friend-list__name">{invite.email}</span>
                  <span className="muted">Sent {formatDate(invite.createdAt)}</span>
                </span>
                <span className="friend-list__actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    disabled={busy}
                    onClick={() => void act(() => api.deleteHouseholdInvite(invite.id))}
                  >
                    Cancel
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function InviteForm({ onSent, suggestions }: { onSent: () => void; suggestions: readonly Friend[] }) {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await invite(email.trim());
  }

  async function invite(address: string) {
    if (!address) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const outcome = await api.inviteToHousehold(address);
      setMessage(OUTCOME_MESSAGES[outcome](address));
      setEmail('');
      onSent();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="new-category" onSubmit={(event) => void handleSubmit(event)}>
      <label htmlFor={inputId} className="visually-hidden">
        Email address of someone to invite
      </label>
      <div className="new-category__row">
        <input
          id={inputId}
          type="email"
          placeholder="Email address of someone you live with"
          autoComplete="off"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={saving}
        />
        <button type="submit" className="btn btn--primary" disabled={saving || !email.trim()}>
          {saving ? 'Inviting…' : 'Invite'}
        </button>
      </div>
      {message && (
        <p className="success-text" role="status">
          {message}
        </p>
      )}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      {suggestions.length > 0 && (
        <div className="invite-suggestions">
          <p className="muted">Already friends? Invite them with a tap:</p>
          <div className="category-toggles">
            {suggestions.map((friend) => (
              <button
                key={friend.id}
                type="button"
                className="category-toggle"
                disabled={saving}
                title={friend.email}
                onClick={() => void invite(friend.email)}
              >
                {friend.name ?? friend.email}
              </button>
            ))}
          </div>
        </div>
      )}
    </form>
  );
}
